import { useState, useEffect } from "react";
import { Plus, Trash2, Search, Link2, Layers, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductUpsell, type LinkType } from "@/hooks/useProductUpsell";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";

export default function AdicionaisPage() {
  const { productsWithUpsell, isLoadingProducts, createLink, deleteLink, isCreating, isDeleting } = useProductUpsell();
  const { products, fetchProducts, isLoading: isLoadingProductsList } = useProducts();
  const { categories, fetchCategories } = useCategories();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null);
  const [searchProduct, setSearchProduct] = useState("");

  // Form state
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [linkType, setLinkType] = useState<LinkType>("category");
  const [linkedCategoryId, setLinkedCategoryId] = useState<string>("");
  const [linkedProductId, setLinkedProductId] = useState<string>("");
  const [minSelections, setMinSelections] = useState(0);
  const [maxSelections, setMaxSelections] = useState(5);
  const [isRequired, setIsRequired] = useState(false);
  const [label, setLabel] = useState("");

  const resetForm = () => {
    setSelectedProductId("");
    setLinkType("category");
    setLinkedCategoryId("");
    setLinkedProductId("");
    setMinSelections(0);
    setMaxSelections(5);
    setIsRequired(false);
    setLabel("");
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const handleAddLink = async () => {
    if (!selectedProductId) return;
    
    if (linkType === "category" && !linkedCategoryId) return;
    if (linkType === "product" && !linkedProductId) return;

    await createLink({
      product_id: selectedProductId,
      link_type: linkType,
      linked_category_id: linkType === "category" ? linkedCategoryId : undefined,
      linked_product_id: linkType === "product" ? linkedProductId : undefined,
      min_selections: minSelections,
      max_selections: maxSelections,
      is_required: isRequired,
      label: label || undefined,
    });

    setAddDialogOpen(false);
    resetForm();
  };

  const handleDeleteLink = (linkId: string) => {
    setLinkToDelete(linkId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (linkToDelete) {
      await deleteLink(linkToDelete);
      setDeleteDialogOpen(false);
      setLinkToDelete(null);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  // Filter products for search
  const filteredProductsForSelect = products.filter(p => 
    p.name.toLowerCase().includes(searchProduct.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Upsell / Adicionais</h1>
          <p className="text-muted-foreground">
            Configure quais categorias ou produtos aparecem como adicionais em cada item
          </p>
        </div>
        <Button onClick={handleOpenAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Vínculo
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex items-start gap-4 py-4">
          <Layers className="h-8 w-8 text-primary flex-shrink-0" />
          <div className="space-y-2">
            <p className="font-medium">Como cadastrar adicionais</p>
            <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
              <li>Cadastre os itens adicionais como produtos normais em <strong>Produtos</strong></li>
              <li>Crie uma categoria para eles (ex: "Adicionais" ou "Extras")</li>
              <li>Volte aqui e vincule essa categoria ao produto principal</li>
            </ol>
            <p className="text-sm text-muted-foreground">
              Os produtos vinculados podem ser vendidos individualmente no PDV.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoadingProducts && (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoadingProducts && productsWithUpsell.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Link2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum vínculo de upsell</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Vincule categorias ou produtos como adicionais para aumentar o ticket médio. 
              Os produtos vinculados aparecerão como opções quando o cliente selecionar o item principal.
            </p>
            <Button onClick={handleOpenAddDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeiro Vínculo
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Products with upsell links */}
      {!isLoadingProducts && productsWithUpsell.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {productsWithUpsell.map((product) => (
            <Card key={product.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                    <CardDescription>
                      {product.upsellLinks.length} vínculo(s) de upsell
                    </CardDescription>
                  </div>
                  <Badge variant="outline">{formatPrice(product.price)}</Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {product.upsellLinks.map((link: any) => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {link.link_type === "category" ? (
                          <Badge variant="secondary">Categoria</Badge>
                        ) : (
                          <Badge variant="outline">Produto</Badge>
                        )}
                        <span className="font-medium">
                          {link.linked_category?.name || link.linked_product?.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {link.is_required ? "Obrigatório" : "Opcional"} • 
                        {link.min_selections === link.max_selections
                          ? ` Exatamente ${link.min_selections}`
                          : ` ${link.min_selections} a ${link.max_selections}`} seleções
                        {link.label && ` • "${link.label}"`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteLink(link.id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Link Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Novo Vínculo de Upsell</DialogTitle>
            <DialogDescription>
              Escolha um produto principal e vincule categorias ou produtos como adicionais
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
            <div className="space-y-4 py-2">
              {/* Product Selection */}
              <div className="space-y-2">
                <Label>Produto Principal *</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar produto..."
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <ScrollArea className="h-40 border rounded-md">
                  {isLoadingProductsList ? (
                    <div className="flex items-center justify-center h-full py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Carregando produtos...</span>
                    </div>
                  ) : filteredProductsForSelect.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {searchProduct ? "Nenhum produto encontrado" : "Nenhum produto cadastrado"}
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredProductsForSelect.map((p) => (
                        <div
                          key={p.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            selectedProductId === p.id
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => setSelectedProductId(p.id)}
                        >
                          <span>{p.name}</span>
                          <span className="text-sm opacity-70">{formatPrice(p.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>

            {/* Link Type */}
            <div className="space-y-2">
              <Label>Tipo de Vínculo *</Label>
              <Select value={linkType} onValueChange={(v: LinkType) => setLinkType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Categoria (todos os produtos)</SelectItem>
                  <SelectItem value="product">Produto Específico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category or Product Selection */}
            {linkType === "category" ? (
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select value={linkedCategoryId} onValueChange={setLinkedCategoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Todos os produtos ativos desta categoria aparecerão como opções
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Produto *</Label>
                <Select value={linkedProductId} onValueChange={setLinkedProductId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} - {formatPrice(p.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Min/Max Selections */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mínimo de Seleções</Label>
                <Input
                  type="number"
                  min={0}
                  value={minSelections}
                  onChange={(e) => setMinSelections(parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>Máximo de Seleções</Label>
                <Input
                  type="number"
                  min={1}
                  value={maxSelections}
                  onChange={(e) => setMaxSelections(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            {/* Required */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Obrigatório</Label>
                <p className="text-xs text-muted-foreground">
                  Cliente deve selecionar pelo menos {minSelections || 1} opção
                </p>
              </div>
              <Switch
                checked={isRequired}
                onCheckedChange={(checked) => {
                  setIsRequired(checked);
                  if (checked && minSelections === 0) setMinSelections(1);
                }}
              />
            </div>

              {/* Label */}
              <div className="space-y-2">
                <Label>Rótulo Personalizado (opcional)</Label>
                <Input
                  placeholder="Ex: Escolha seus adicionais"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddLink} 
              disabled={
                isCreating || 
                !selectedProductId || 
                (linkType === "category" && !linkedCategoryId) ||
                (linkType === "product" && !linkedProductId)
              }
            >
              {isCreating ? "Salvando..." : "Criar Vínculo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover vínculo de upsell?</AlertDialogTitle>
            <AlertDialogDescription>
              O vínculo será removido e os adicionais não aparecerão mais para este produto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
